import React, { useState, useEffect, useRef } from "react";
import HeaderWithSearch from "./HeaderWithSearch";
import "./TaskManager.css";

const STORAGE_KEY = "vmcreations-tasks-v1";
const STORAGE_ID_KEY = "vmcreations-tasks-nextid-v1";

function loadLocalTasks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalTasks(tasks) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
}

function nextLocalId() {
  const n = parseInt(localStorage.getItem(STORAGE_ID_KEY) || "1", 10);
  const id = Number.isFinite(n) ? n : 1;
  localStorage.setItem(STORAGE_ID_KEY, String(id + 1));
  return id;
}

function sortTasks(tasks) {
  const done = (t) => (t.completed === true || t.completed === 1 ? 1 : 0);
  return [...tasks].sort((a, b) => {
    const da = done(a);
    const db = done(b);
    if (da !== db) return da - db;
    return Number(a.id) - Number(b.id);
  });
}

export default function TaskManager() {
  const [tasks, setTasks] = useState(() => sortTasks(loadLocalTasks()));
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const addTask = (name) => {
    setTasks((prev) => {
      const id = nextLocalId();
      const next = sortTasks([...prev, { id, name, completed: false }]);
      saveLocalTasks(next);
      return next;
    });
  };

  const toggleTask = (id) => {
    setTasks((prev) => {
      const next = prev.map((t) =>
        t.id === id ? { ...t, completed: !t.completed } : t
      );
      const sorted = sortTasks(next);
      saveLocalTasks(sorted);
      return sorted;
    });
  };

  const removeTask = (id) => {
    setTasks((prev) => {
      const next = prev.filter((t) => t.id !== id);
      const sorted = sortTasks(next);
      saveLocalTasks(sorted);
      return sorted;
    });
  };

  const clearCompleted = () => {
    setTasks((prev) => {
      const next = prev.filter((t) => !t.completed);
      const sorted = sortTasks(next);
      saveLocalTasks(sorted);
      return sorted;
    });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const raw = inputRef.current?.value?.trim() || "";
    if (!raw) return;
    addTask(raw);
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.focus();
  };

  const incomplete = tasks.filter((t) => !t.completed).length;
  const completedCount = tasks.length - incomplete;

  return (
    <>
      <HeaderWithSearch />
      <div className="task-manager-page">
        <div className="task-manager-root task-manager-inner">
          <header className="tm-header">
            <h1 className="tm-title">Task manager</h1>
            <p className="tm-subtitle">
              Add tasks, mark them done, or remove them. Your list is saved in
              this browser on your device—no extra setup.
            </p>
            <span className="tm-badge">Saved in this browser</span>
          </header>

          <div className="tm-card">
            <form className="tm-form" onSubmit={onSubmit}>
              <label className="sr-only" htmlFor="task-name-input">
                New task
              </label>
              <input
                ref={inputRef}
                id="task-name-input"
                type="text"
                name="name"
                autoComplete="off"
                placeholder="What needs doing?"
              />
              <button type="submit" className="tm-btn tm-btn-primary">
                Add task
              </button>
            </form>

            <ul className="tm-list" aria-live="polite">
              {tasks.map((t) => (
                <li
                  key={t.id}
                  className={`tm-item${t.completed ? " completed" : ""}`}
                >
                  <span className="tm-name">{t.name}</span>
                  <div className="tm-actions">
                    <button
                      type="button"
                      className="tm-btn tm-btn-ghost"
                      onClick={() => toggleTask(t.id)}
                      aria-label={t.completed ? "Mark not done" : "Mark done"}
                    >
                      {t.completed ? "Undo" : "Done"}
                    </button>
                    <button
                      type="button"
                      className="tm-btn tm-btn-danger"
                      onClick={() => removeTask(t.id)}
                    >
                      Delete
                    </button>
                  </div>
                </li>
              ))}
            </ul>
            {tasks.length === 0 ? (
              <p className="tm-empty" id="tm-empty">
                No tasks yet. Add one above.
              </p>
            ) : null}

            <div className="tm-toolbar">
              <span className="tm-count">
                {incomplete} active
                {completedCount > 0
                  ? ` · ${completedCount} completed`
                  : ""}
              </span>
              <button
                type="button"
                className="tm-btn tm-btn-ghost"
                onClick={clearCompleted}
                disabled={completedCount === 0}
              >
                Clear completed
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
